"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { Star } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SocialProof() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const reviews = [{
    quote: "They transformed our overgrown backyard into a pristine sanctuary. Truly professional arborists.",
    author: "Robert M.",
    role: "Homeowner",
    date: "15 MAR 2025",
    initials: "RM",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/client_robert_portrait.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/client_robert_portrait.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/client_robert_portrait.png-wvc-sizes"
  }, {
    quote: "The team's knowledge of tree health is unmatched. They saved our heritage oak tree.",
    author: "Sarah L.",
    role: "Homeowner",
    date: "02 FEB 2025",
    initials: "SL",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/client_sarah_portrait.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/client_sarah_portrait.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/client_sarah_portrait.webp-wvc-sizes"
  }, {
    quote: "Prompt, efficient, and left the site cleaner than they found it. Highly recommended service.",
    author: "David K.",
    role: "Property Manager",
    date: "21 JAN 2025",
    initials: "DK",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/client_david_portrait.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/client_david_portrait.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/client_david_portrait.png-wvc-sizes"
  }];
  return <section data-section-id="1693" id="social-proof" className="bg-background text-foreground relative overflow-hidden py-28 md:py-40">
      <div className="mx-auto w-full max-w-[1320px] px-5 sm:px-8 lg:px-14">
        {}
        <div ref={headerRef} className={`relative grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {}
          <div aria-hidden="true" className="pointer-events-none absolute -top-8 left-0 lg:left-[-1rem] font-serif font-light leading-none select-none" style={{
          fontSize: "clamp(8rem, 16vw, 14rem)",
          color: "transparent",
          WebkitTextStroke: "1px hsl(30 15% 25% / 0.15)",
          opacity: 0.4
        }}>
            04
          </div>

          {}
          <div className="lg:col-span-9 relative z-10 order-2 lg:order-1">
            {}
            <div className="flex items-center gap-4 mb-8">
              <span className="font-mono text-[11px] sm:text-[13px] uppercase tracking-[0.18em] font-medium text-primary">
                § N° 04 — Client Testimonials · Verified Feedback
              </span>
            </div>
            <div className="flex items-center gap-2 mb-10">
              <span className="h-px w-14 bg-accent" />
              <span className="size-1 rounded-md bg-accent" />
            </div>

            {}
            <h2 className="font-default font-normal leading-[0.98] tracking-tight text-foreground" style={{
            fontSize: "clamp(2.25rem, 4.6vw, 4.25rem)"
          }}>
              Five-star service,{" "}
              <em className="italic relative inline-block text-accent">
                consistently
                <span className={`absolute left-0 -bottom-2 h-px transition-all duration-700 ease-out ${headerVisible ? "w-full" : "w-0"}`} style={{
                backgroundColor: "var(--accent)",
                transitionDelay: "300ms"
              }} />
              </em>{" "}
              delivered.
            </h2>
          </div>

          {}
          <div className="lg:col-span-3 relative z-10 order-1 lg:order-2 flex flex-col items-start lg:items-end lg:text-right">
            <div className="font-default font-light leading-none text-accent" style={{
            fontSize: "5rem"
          }}>
              5.0
            </div>
            <div className="flex items-center gap-1 mt-4 mb-4">
              {[0, 1, 2, 3, 4].map(i => <Star key={i} data-index={i} className="size-5 text-accent fill-accent" />)}
            </div>
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] font-medium text-foreground/70">
              250+ Verified Reviews · Rated Excellent
            </span>
          </div>
        </div>

        {}
        <div ref={cardsRef} className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {reviews.map((review, i) => <Card key={i} data-index={i} className={`group relative rounded-lg p-9 transition-all duration-500 ease-out hover:-translate-y-1 ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          backgroundColor: "var(--card)",
          border: "1px solid var(--border)",
          transitionDelay: `${i * 120}ms`,
          color: "var(--card-foreground)"
        }}>
              {}
              <span aria-hidden="true" className="absolute top-3 left-4 font-mono text-[11px] text-foreground/30">
                42.36° N
              </span>
              <span aria-hidden="true" className="absolute top-3 right-4 font-mono text-[11px] text-foreground/30">
                71.05° W
              </span>
              <span aria-hidden="true" className="absolute bottom-3 left-4 font-mono text-[11px] text-foreground/30">
                42.36° N
              </span>
              <span aria-hidden="true" className="absolute bottom-3 right-4 font-mono text-[11px] text-foreground/30">
                71.05° W
              </span>

              {}
              <div className="flex items-center gap-3 mb-7">
                <div className="flex items-center gap-0.5">
                  {[0, 1, 2, 3, 4].map(s => <Star key={s} className="size-3.5 text-accent fill-accent" />)}
                </div>
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] font-medium text-foreground/70">
                  · Google
                </span>
              </div>

              {}
              <blockquote className="font-default italic font-normal leading-snug text-foreground" style={{
            fontSize: "clamp(1.25rem, 1.6vw, 1.5rem)"
          }}>
                &ldquo;{review.quote}&rdquo;
              </blockquote>

              {}
              <div className="flex items-center gap-2 mt-8 mb-6">
                <span className="size-1 rounded-md bg-accent" />
                <span className="h-px w-10 bg-accent" />
              </div>

              {}
              <div className="flex items-center gap-4">
                <SafeHydrate><Avatar className="size-10 rounded-lg bg-muted">
                  <AvatarImage src={review.avatar} data-wvc-srcset={review.srcset} data-wvc-sizes={review.sizes} alt={review.author} loading="lazy" />
                  <AvatarFallback className="font-mono text-xs bg-muted text-primary">
                    {review.initials}
                  </AvatarFallback>
                </Avatar></SafeHydrate>
                <div className="flex flex-col">
                  <span className="font-default font-semibold text-base text-foreground">
                    {review.author}
                  </span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.08em] mt-0.5 text-foreground/60">
                    {review.date}
                  </span>
                </div>
              </div>
            </Card>)}
        </div>

        {}
        <div className="mt-16 flex items-center gap-2">
          <span className="font-mono text-[11px] text-foreground/50">
            42.3601° N / 71.0589° W
          </span>
        </div>
      </div>
    </section>;
}